if (!customElements.get('glyc-card-product')) {
  customElements.define(
    'glyc-card-product',
    class GlycCardProduct extends HTMLElement {
      connectedCallback() {
        this.productData = this.readProductData();
        if (!this.productData) return;

        this.formId = this.productData.formId;
        this.form = this.findProductForm();
        if (!this.form) return;

        this.variantInput = this.form.querySelector('[name="id"]');
        this.quantityInput = this.form.querySelector('[data-glyc-quantity-field]');
        this.buyNowRoot = this.form.closest('product-form')?.querySelector('[data-glyc-buy-now]');
        this.priceRoot = this.findPriceRoot();
        this.inventoryRoot = this.findInventoryRoot();
        this.submitButton = this.form.closest('product-form')?.querySelector('[type="submit"]');
        this.submitText = this.submitButton?.querySelector('span:not(.sold-out-message)');
        this.strings = window.glycCardStrings || {};
        this.variantStrings = window.variantStrings || {};

        this.querySelectorAll('[data-glyc-option]').forEach((input) => {
          input.addEventListener('change', () => this.onOptionChange());
        });

        this.syncSelectedLabels();
        this.updateForVariant(this.getSelectedVariant());
      }

      readProductData() {
        const node = this.querySelector('[data-glyc-product-json]');
        if (!node) return null;

        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.warn('GLYC card product: invalid product JSON', error);
          return null;
        }
      }

      findProductForm() {
        const selector = this.formId ? `#${CSS.escape(this.formId)}` : 'form[data-type="add-to-cart-form"]';
        return document.querySelector(selector);
      }

      findPriceRoot() {
        if (!this.formId) return null;
        return document.querySelector(`[data-glyc-price-root="${this.formId}"]`);
      }

      findInventoryRoot() {
        if (!this.formId) return null;
        return document.querySelector(`[data-glyc-inventory-root="${this.formId}"]`);
      }

      getSelectedOptions() {
        const options = [];
        this.querySelectorAll('[data-glyc-option]:checked').forEach((input) => {
          const position = Number(input.dataset.glycOption);
          options[position - 1] = input.value;
        });
        return options;
      }

      getSelectedVariant() {
        const selected = this.getSelectedOptions();
        if (selected.includes(undefined)) return undefined;

        return this.productData.variants.find((variant) =>
          variant.options.every((option, index) => option === selected[index])
        );
      }

      onOptionChange() {
        this.syncSelectedLabels();
        this.updateForVariant(this.getSelectedVariant());
      }

      syncSelectedLabels() {
        this.querySelectorAll('.glyc-card-variant-picker__option').forEach((fieldset) => {
          fieldset.querySelectorAll('.glyc-card-variant-picker__label').forEach((label) => {
            const input = label.querySelector('[data-glyc-option]');
            label.classList.toggle('glyc-card-variant-picker__label--selected', input?.checked);
          });
        });
      }

      updateForVariant(variant) {
        if (!this.variantInput) return;

        if (!variant) {
          this.variantInput.disabled = true;
          this.updateInventoryMessage(null);
          this.updateQuantityInput(null);
          this.updateBuyNow(null);
          this.setSubmitState('unavailable');
          this.dispatchVariantChange(null);
          this.announceStatus(this.variantStrings.unavailable);
          return;
        }

        this.variantInput.value = variant.id;
        this.variantInput.disabled = !variant.available;

        if (this.priceRoot) {
          const amount = this.priceRoot.querySelector('[data-glyc-price-amount]');
          const compare = this.priceRoot.querySelector('[data-glyc-price-compare]');

          if (amount) amount.textContent = variant.price_formatted;

          if (compare) {
            if (variant.compare_at_price > variant.price) {
              compare.textContent = variant.compare_at_price_formatted;
              compare.hidden = false;
            } else {
              compare.textContent = '';
              compare.hidden = true;
            }
          }
        }

        this.updateInventoryMessage(variant);
        this.updateQuantityInput(variant);
        this.updateBuyNow(variant);

        if (variant.available) {
          this.setSubmitState('available');
        } else {
          this.setSubmitState('sold_out');
        }

        this.dispatchVariantChange(variant);

        this.announceStatus(this.strings.variantStatusUpdated);
      }

      dispatchVariantChange(variant) {
        this.dispatchEvent(
          new CustomEvent('glyc:variant-change', {
            bubbles: true,
            detail: { variant },
          })
        );
      }

      updateQuantityInput(variant) {
        if (!this.quantityInput) return;

        if (!variant?.quantity_rule) return;

        const min = Number(variant.quantity_rule.min);
        const increment = Number(variant.quantity_rule.increment);
        const max = variant.quantity_rule.max != null ? Number(variant.quantity_rule.max) : null;

        this.quantityInput.min = String(min);
        this.quantityInput.step = String(increment);
        this.quantityInput.dataset.min = String(min);
        this.quantityInput.dataset.step = String(increment);

        if (max != null) {
          this.quantityInput.max = String(max);
          this.quantityInput.dataset.max = String(max);
        } else {
          this.quantityInput.removeAttribute('max');
          this.quantityInput.dataset.max = '';
        }

        let value = Number.parseInt(this.quantityInput.value, 10);
        if (Number.isNaN(value) || value < min) value = min;
        if (max != null && value > max) value = max;
        this.quantityInput.value = String(value);
      }

      updateBuyNow(variant) {
        if (!this.buyNowRoot) return;

        const isAvailable = Boolean(variant?.available);
        this.buyNowRoot.hidden = !isAvailable;

        this.buyNowRoot.querySelectorAll('button, a').forEach((control) => {
          if (isAvailable) {
            control.removeAttribute('disabled');
            control.removeAttribute('aria-disabled');
          } else {
            control.setAttribute('disabled', 'disabled');
            control.setAttribute('aria-disabled', 'true');
          }
        });
      }

      getInventoryMessageElement() {
        if (!this.inventoryRoot) return null;

        let message = this.inventoryRoot.querySelector('[data-glyc-inventory-message]');
        if (!message) {
          message = this.buildLaunchingSoonNotice();
          this.inventoryRoot.appendChild(message);
        }

        return message;
      }

      buildLaunchingSoonNotice() {
        const strings = window.glycLaunchingSoonStrings || {};
        const wrapper = document.createElement('div');
        wrapper.className = 'glyc-stack glyc-stack--sm glyc-inventory-status--unavailable';
        wrapper.setAttribute('data-glyc-inventory-message', '');

        const heading = document.createElement('p');
        heading.className = 'glyc-collection-card__title';
        heading.textContent = strings.heading || 'Launching Soon';
        wrapper.appendChild(heading);

        [strings.line1, strings.line2].forEach((line) => {
          if (!line) return;
          const paragraph = document.createElement('p');
          paragraph.className = 'glyc-collection-card__summary';
          paragraph.textContent = line;
          wrapper.appendChild(paragraph);
        });

        return wrapper;
      }

      getInventoryBaseClass() {
        return this.inventoryRoot?.dataset.glycInventoryClass || 'glyc-collection-card__inventory';
      }

      updateInventoryMessage(variant) {
        const messageEl = this.getInventoryMessageElement();
        if (!messageEl) return;

        const baseClass = this.getInventoryBaseClass();

        if (!variant) {
          messageEl.replaceWith(this.buildSimpleInventoryMessage(baseClass, 'glyc-inventory-status--unavailable', this.variantStrings.unavailable || 'Unavailable'));
          return;
        }

        if (!variant.available) {
          const notice = this.buildLaunchingSoonNotice();
          if (baseClass) notice.classList.add(baseClass);
          messageEl.replaceWith(notice);
          return;
        }

        const simpleMessage = this.buildSimpleInventoryMessage(baseClass, '', '');
        messageEl.replaceWith(simpleMessage);

        const managed = variant.inventory_management === 'shopify';
        const lowStock =
          managed &&
          variant.inventory_policy === 'deny' &&
          variant.inventory_quantity > 0 &&
          variant.inventory_quantity <= 10;

        if (lowStock) {
          const template = this.strings.inventoryLowStock || 'Low stock: [quantity] left';
          simpleMessage.textContent = template.replace('[quantity]', variant.inventory_quantity);
          simpleMessage.classList.add('glyc-inventory-status--low');
          return;
        }

        if (managed) {
          simpleMessage.textContent = this.strings.inventoryInStock || 'In stock';
          simpleMessage.classList.add('glyc-inventory-status--available');
        } else {
          simpleMessage.textContent = '';
        }
      }

      buildSimpleInventoryMessage(baseClass, statusClass, text) {
        const message = document.createElement('p');
        message.className = ['glyc-inventory-status', statusClass, baseClass].filter(Boolean).join(' ');
        message.setAttribute('data-glyc-inventory-message', '');
        message.textContent = text;
        return message;
      }

      setSubmitState(state) {
        if (!this.submitButton) return;

        if (state === 'available') {
          this.submitButton.removeAttribute('disabled');
          if (this.submitText) {
            this.submitText.textContent = this.variantStrings.addToCart || 'Add to cart';
          }
          return;
        }

        this.submitButton.setAttribute('disabled', 'disabled');

        if (!this.submitText) return;

        if (state === 'sold_out') {
          this.submitText.textContent = this.variantStrings.soldOut || 'Sold out';
        } else {
          this.submitText.textContent = this.variantStrings.unavailable || 'Unavailable';
        }
      }

      announceStatus(message) {
        if (!message || !this.inventoryRoot) return;
        this.inventoryRoot.setAttribute('aria-label', message);
      }
    }
  );
}
